import { Center, ProgressCircle, Table } from '@chakra-ui/react';
import { setDocumentTitle } from '../../../utils/documentTitle';
import { useEffect, useMemo, useState } from 'react';
import { useOfferList } from '../../../hooks/useOffer';

type SortColumn = 'code' | 'type' | 'subject' | 'level' | 'price';

type OfferColumn = {
	key: SortColumn;
	label: string;
};

const columns: OfferColumn[] = [
	{ key: 'code', label: 'Kod' },
	{ key: 'type', label: 'Typ' },
	{ key: 'subject', label: 'Przedmiot' },
	{ key: 'level', label: 'Poziom' },
	{ key: 'price', label: 'Cena' },
];

const OfferPage = () => {
	useEffect(setDocumentTitle('Oferta')); // eslint-disable-line react-hooks/exhaustive-deps

	const [offer, offerLoading, offerError] = useOfferList();
	const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
	const [sortColumn, setSortColumn] = useState<SortColumn>('code');

	const handleSort = (column: SortColumn) => {
		if (column === sortColumn) {
			setSortOrder((previous) => (previous === 'asc' ? 'desc' : 'asc'));
			return;
		}

		setSortColumn(column);
		setSortOrder('asc');
	};

	const getSortValue = (item: any, column: SortColumn) => {
		switch (column) {
			case 'code':
				return item?.code ?? '';
			case 'type':
				return item?.type ?? '';
			case 'subject':
				return item?.subject ?? '';
			case 'level':
				return item?.level ?? '';
			case 'price':
				return Number(item?.price.value ?? 0);
			default:
				return '';
		}
	};

	const sortedOffer = useMemo(() => {
		if (!offer?.length) {
			return [];
		}

		return [...offer].sort((a, b) => {
			const left = getSortValue(a, sortColumn);
			const right = getSortValue(b, sortColumn);

			if (typeof left === 'number' && typeof right === 'number') {
				return sortOrder === 'asc' ? left - right : right - left;
			}

			const comparison = String(left).localeCompare(String(right), 'pl', { sensitivity: 'base' });
			return sortOrder === 'asc' ? comparison : -comparison;
		});
	}, [offer, sortColumn, sortOrder]);

	if (offerLoading) {
		return (
			<Center my={12}>
				<ProgressCircle.Root value={null}>
					<ProgressCircle.Circle css={{ '--thickness': '12px' }}>
						<ProgressCircle.Track />
						<ProgressCircle.Range stroke="orange.400" />
					</ProgressCircle.Circle>
				</ProgressCircle.Root>
			</Center>
		);
	}

	if (offerError) {
		console.error(offerError);
		return null;
	}

	if (!offer?.length) {
		return <Center my={12}>Brak ofert.</Center>;
	}

	const sortLabel = (column: SortColumn, label: string) => {
		const isActive = sortColumn === column;
		const arrow = isActive ? (sortOrder === 'asc' ? ' ▲' : ' ▼') : '';
		return `${label}${arrow}`;
	};

	const formatPrice = (item: any) => {
		if (!item?.price) {
			return '';
		}

		const unit = item.price.unit === 'hour' ? 'godz' : item.price.unit === 'month' ? 'm-c' : '';
		const suffix = unit ? `/${unit}` : '';
		return `${item.price.value} zł${suffix}`;
	};

	return (
		<Table.ScrollArea>
			<Table.Root size="sm">
				<Table.Header>
					<Table.Row>
						{columns.map((column) => (
							<Table.ColumnHeader
								key={column.key}
								onClick={() => handleSort(column.key)}
								cursor="pointer"
								userSelect="none"
								fontWeight="bold"
							>
								{sortLabel(column.key, column.label)}
							</Table.ColumnHeader>
						))}
					</Table.Row>
				</Table.Header>
				<Table.Body>
					{sortedOffer.map((item: any, index) => (
						<Table.Row key={`${item.code ?? index}`}>
							{columns.map((column) => (
								<Table.Cell key={`${item.code ?? index}-${column.key}`}>
									{column.key === 'price' ? formatPrice(item) : item[column.key]}
								</Table.Cell>
							))}
						</Table.Row>
					))}
				</Table.Body>
			</Table.Root>
		</Table.ScrollArea>
	);
};

export default OfferPage;